import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { darcula } from "react-syntax-highlighter/dist/esm/styles/prism";
import { FaCopy } from "react-icons/fa";
import "./SimpleParagraph.css";
import * as XLSX from "xlsx";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const CodeBlock = ({ language, value }) => {
  const [copied, setCopied] = useState(false);

  return (
    <div className="code-block" style={{ position: "relative" }}>
      <CopyToClipboard text={value} onCopy={() => setCopied(true)}>
        <button className="copy-button rounded d-flex align-items-center">
          {copied ? (
            "Copiado"
          ) : (
            <div className="d-flex align-items-center gap-2">
              <FaCopy className="" /><small className="">Copiar código</small>
            </div>
          )}
        </button>
      </CopyToClipboard>
      <SyntaxHighlighter language={language} style={darcula}>
        {value}
      </SyntaxHighlighter>
    </div>
  );
};

const handleUserTags = (text) => {
  const regex = /<user_code>(.*?)<\/user_code>/gs;
  return text.replace(regex, (match, p1) => p1);
};

const handleSquareBracketTags = (text) => {
  const regex = /【(.*?)】/gs;
  return text.replace(regex, "");
};

const processSheetTag = (text, setSheetContent, setSheetProcessing) => {
  const openSheetTagRegex = /<sheet>[\s\S]*/;
  const closeSheetTagRegex = /<\/sheet>/;

  const openSheetTagMatch = openSheetTagRegex.exec(text);
  const closeSheetTagMatch = closeSheetTagRegex.exec(text);

  if (openSheetTagMatch && !closeSheetTagMatch) {
    setSheetProcessing(true);
    setSheetContent(
      (prev) => prev + text.substring(openSheetTagMatch[0].length)
    );
    return text.replace(openSheetTagRegex, "");
  } else if (closeSheetTagMatch) {
    const sheetTagRegex = /<sheet>([\s\S]*?)<\/sheet>/s;
    const sheetTagMatch = sheetTagRegex.exec(text);
    if (sheetTagMatch) {
      setSheetContent(sheetTagMatch[1]);
      setSheetProcessing(false);
      return text.replace(sheetTagMatch[0], "");
    }
  }
  return text;
};

const generateSheetUrl = (sheetContent, setSheetDownloadUrl) => {
  try {
    const jsonData = JSON.parse(sheetContent);
    const worksheet = XLSX.utils.json_to_sheet(jsonData.rows || jsonData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    setSheetDownloadUrl(url);
  } catch (error) {
    console.error("Error generating sheet:", error);
  }
};

const processDocxTag = (text, setDocxContent, setDocxProcessing) => {
  const openDocxTagRegex = /<docx id="([^"]+)"(?:\s+language="[^"]*")?>[\s\S]*/;
  const closeDocxTagRegex = /<\/docx>/;

  const openDocxTagMatch = openDocxTagRegex.exec(text);
  const closeDocxTagMatch = closeDocxTagRegex.exec(text);

  if (openDocxTagMatch && !closeDocxTagMatch) {
    setDocxProcessing(true);
    setDocxContent((prev) => prev + text.substring(openDocxTagMatch[0].length));
    return text.replace(openDocxTagRegex, "");
  } else if (closeDocxTagMatch) {
    const docxTagRegex =
      /<docx id="([^"]+)"(?:\s+language="[^"]*")?>([\s\S]*?)<\/docx>/s;
    const docxTagMatch = docxTagRegex.exec(text);
    if (docxTagMatch) {
      setDocxContent(docxTagMatch[2]);
      setDocxProcessing(false);
      return text.replace(docxTagMatch[0], "");
    }
  }
  return text;
};

const processAllDocxTags = (text) => {
  const docxTagRegex = /<docx id="([^"]+)"(?:\s+language="([^"]*)")?>([\s\S]*?)<\/docx>/g;

  let newText = text;
  let match;

  while ((match = docxTagRegex.exec(text)) !== null) {
    const [fullMatch] = match;
    newText = newText.replace(fullMatch, "");
  }

  return { newText };
};

const generateDocxUrl = async (
  docxContent,
  setDocxDownloadUrl,
  templateId,
  language
) => {
  try {
    const jsonData = JSON.parse(docxContent);
    const templatePath = `/assets/templates/${templateId}.${language}.template.docx`;

    const response = await fetch(templatePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch template: ${response.statusText}`);
    }
    const content = await response.arrayBuffer();
    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
    });

    doc.setData(jsonData);

    try {
      doc.render();
    } catch (error) {
      if (error instanceof Error) {
        const e = {
          message: error.message,
          name: error.name,
          stack: error.stack,
          properties: error.properties,
        };
        console.log(JSON.stringify({ error: e }));
        throw error;
      } else {
        console.log("An unexpected error occurred");
        throw error;
      }
    }

    const out = doc.getZip().generate({ type: "blob" });
    const url = URL.createObjectURL(out);
    setDocxDownloadUrl(url);
  } catch (error) {
    console.error("Error generating docx:", error);
  }
};

const MessageComponent = ({ role, children }) => {
  const className = role === "user" ? "user-message" : "assistant-message, pt-2, pb-2";
  return (
    <ReactMarkdown
      className={className}
      remarkPlugins={[remarkGfm]}
      components={{
        code({ node, inline, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || "");
          return !inline && match ? (
            <CodeBlock
              language={match[1]}
              value={String(children).replace(/\n$/, "")}
              {...props}
            />
          ) : (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
        table({ node, ...props }) {
          return (
            <div className="markdown-table mb-5 ">
              <table {...props} />
            </div>
          );
        },
        p({ node, ...props }) {
          return <p {...props} className="mb-2 " />;
        },
        ul({ node, ...props }) {
          return <ul {...props} className="mb-2 " />;
        }
      }}
    >
      {children}
    </ReactMarkdown>
  );
};

const SimpleParagraph = ({ className = "", msg }) => {
  const [sheetContent, setSheetContent] = useState("");
  const [sheetDownloadUrl, setSheetDownloadUrl] = useState("");
  const [sheetProcessing, setSheetProcessing] = useState(false);
  const [docxContent, setDocxContent] = useState("");
  const [docxDownloadUrl, setDocxDownloadUrl] = useState("");
  const [docxProcessing, setDocxProcessing] = useState(false);
  const [processedContent, setProcessedContent] = useState(msg.content);

  const { t } = useTranslation();

  useEffect(() => {
    let content = handleUserTags(msg.content);
    content = handleSquareBracketTags(content);
    content = processSheetTag(content, setSheetContent, setSheetProcessing);
    content = processDocxTag(content, setDocxContent, setDocxProcessing);
    
    const { newText } = processAllDocxTags(content);

    if (newText) {
      setProcessedContent(newText);
    } else {
      setProcessedContent(content);
    }
  }, [msg.content]);

  useEffect(() => {
    if (!sheetProcessing && sheetContent) {
      generateSheetUrl(sheetContent, setSheetDownloadUrl);
    }
  }, [sheetProcessing, sheetContent]);

  useEffect(() => {
    if (!docxProcessing && docxContent) {
      const docxTagMatch = /<docx id="([^"]+)">/.exec(msg.content);
    
      if (docxTagMatch) {
        const templateId = docxTagMatch[1];
        const language = i18n.language || "pt-BR";
        generateDocxUrl(docxContent, setDocxDownloadUrl, templateId, language);
      }
    }
  }, [docxProcessing, docxContent, msg.content]);

  useEffect(() => {
    return () => {
      if (sheetDownloadUrl) {
        URL.revokeObjectURL(sheetDownloadUrl);
      }
      if (docxDownloadUrl) {
        URL.revokeObjectURL(docxDownloadUrl);
      }
    };
  }, [sheetDownloadUrl, docxDownloadUrl]);

  return (
    <div className={className}>
      <MessageComponent role={msg.role}>{processedContent}</MessageComponent>
      {msg.role === "assistant" && (
        <>
          {sheetProcessing && (
            <div className="d-flex align-items-center">
              <div className="loader"></div>
              {t("downloadSheetMessage.loading")}
            </div>
          )}
          {!sheetProcessing && sheetDownloadUrl && (
            <div>
              <a href={sheetDownloadUrl} download="sheet.xlsx" className="btn btn-secondary py-1 mb-2">
                {t("downloadSheetMessage.success")}
              </a>
            </div>
          )}
          {docxProcessing && (
            <div className="d-flex align-items-center">
              <div className="loader"></div>
              {t("downloadDocumentMessage.loading")}
            </div>
          )}
          {!docxProcessing && docxDownloadUrl && (
            <div>
              <a href={docxDownloadUrl} download="document.docx" className="btn btn-secondary py-1 mb-2">
                {t("downloadDocumentMessage.success")}
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SimpleParagraph;